import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function TodayPunchCard({ todayPunch, currentTime }) {
  const navigate = useNavigate();

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const status = todayPunch?.Status || 'Not Punched';
  const isPresent = status.includes('Present') || status.includes('Clocked');

  let badgeColor = 'bg-slate-100 text-slate-700 border-slate-300';
  let dotColor = 'bg-slate-400';

  if (status.includes('Clocked In') || status === 'Present') {
    badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-300';
    dotColor = 'bg-emerald-500 animate-pulse';
  } else if (status.includes('Clocked Out')) {
    badgeColor = 'bg-blue-50 text-blue-800 border-blue-300';
    dotColor = 'bg-blue-500';
  } else if (status === 'Absent') {
    badgeColor = 'bg-rose-50 text-rose-800 border-rose-300';
    dotColor = 'bg-rose-500';
  }

  return (
    <div className="flex flex-col justify-between rounded-3xl border border-outline-variant/25 bg-surface p-6 shadow-xs">
      <div>
        {/* Top Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-xl">timer</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-on-surface">Today's Punch</h3>
              <p className="text-[11px] text-slate-500">Live Attendance Status</p>
            </div>
          </div>

          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${badgeColor}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
            <span>{status}</span>
          </span>
        </div>

        {/* Live Digital Clock */}
        <div className="my-5 rounded-2xl bg-surface-container-high/60 p-4 text-center">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            Current System Time
          </span>
          <div className="font-mono text-2xl sm:text-3xl font-extrabold tracking-tight text-on-surface mt-0.5">
            {formattedTime}
          </div>
          <span className="text-[11px] text-slate-400 font-medium">
            Shift: {todayPunch?.shiftTiming || '09:30 AM - 06:30 PM'}
          </span>
        </div>

        {/* In / Out / Total Hours Metrics */}
        <div className="grid grid-cols-3 gap-2 border-t border-outline-variant/15 pt-4 text-center">
          <div className="rounded-xl bg-surface-container-low p-2.5">
            <span className="text-[10px] uppercase font-bold text-slate-500 block">First In</span>
            <strong className="text-xs font-mono font-extrabold text-on-surface mt-0.5 block truncate">
              {todayPunch?.FirstPunch || '--:--'}
            </strong>
          </div>

          <div className="rounded-xl bg-surface-container-low p-2.5">
            <span className="text-[10px] uppercase font-bold text-slate-500 block">Last Out</span>
            <strong className="text-xs font-mono font-extrabold text-on-surface mt-0.5 block truncate">
              {todayPunch?.LastPunch || '--:--'}
            </strong>
          </div>

          <div className="rounded-xl bg-surface-container-low p-2.5">
            <span className="text-[10px] uppercase font-bold text-slate-500 block">Duration</span>
            <strong className="text-xs font-mono font-extrabold text-on-surface mt-0.5 block truncate">
              {todayPunch?.TotalHours || '--'}
            </strong>
          </div>
        </div>
      </div>

      {/* Footer Action Links */}
      <div className="mt-5 pt-3 border-t border-outline-variant/15 flex items-center justify-between text-xs font-bold">
        <button
          type="button"
          onClick={() => navigate('/self-service/attendance-history')}
          className="text-primary hover:underline inline-flex items-center gap-1 cursor-pointer"
        >
          <span>Attendance History</span>
          <span className="material-symbols-outlined text-sm">arrow_forward</span>
        </button>

        <button
          type="button"
          onClick={() => navigate('/self-service/attendance-regularization/entry')}
          className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 cursor-pointer"
        >
          <span className="material-symbols-outlined text-sm">edit_calendar</span>
          <span>Regularize</span>
        </button>
      </div>
    </div>
  );
}
